/**
 * Base tool of a GA run: the fields a user fills by hand, and their validation.
 *
 * Mirrors `ISO13399MinimalSchema` of the `tool_recommender` worker (keys are its ISO-13399
 * column aliases, ranges and choices are its pandera checks). The worker validates the base tool
 * against that schema and silently drops it when it does not pass — the run then starts
 * unseeded — so nothing invalid must leave this page. `material_name` / `operation_name` belong
 * to the same schema but are set by the run itself, not by the user.
 *
 * Product decision (2026-09-18): every field is mandatory here, including those the worker
 * accepts as null. The list is stable, which is why it lives in the frontend.
 */

export type BaseToolUnit = 'mm' | '°' | '%';

export interface NumberField {
  kind: 'number';
  /** Wire key: the ISO-13399 alias the worker expects. */
  key: string;
  min: number;
  max: number;
  unit: BaseToolUnit;
  /** `min` itself is not allowed (pandera `gt` instead of `ge`). */
  exclusiveMin?: boolean;
}

export interface ChoiceField {
  kind: 'choice';
  key: string;
  options: readonly string[];
  /** The wire value is a number (`Nb dents`), not the option text. */
  numeric?: boolean;
}

export type BaseToolField = NumberField | ChoiceField;

export type BaseToolGroupId = 'dimensions' | 'cutting' | 'angles' | 'grinding' | 'material';

export interface BaseToolGroup {
  id: BaseToolGroupId;
  fields: BaseToolField[];
}

const num = (
  key: string,
  min: number,
  max: number,
  unit: BaseToolUnit,
  exclusiveMin = false
): NumberField => ({ kind: 'number', key, min, max, unit, ...(exclusiveMin && { exclusiveMin }) });

const choice = (key: string, options: readonly string[], numeric = false): ChoiceField => ({
  kind: 'choice',
  key,
  options,
  ...(numeric && { numeric }),
});

export const BASE_TOOL_GROUPS: BaseToolGroup[] = [
  {
    id: 'dimensions',
    fields: [
      num('d1', 0, 6, 'mm', true),
      num('l1', 0, 20, 'mm', true),
      num('Longueur utile maximum LUX [mm]', 0, 20, 'mm'),
      // The worker's alias really has two spaces before "#1".
      num('diam. collet  #1 (D2)', 0, 6, 'mm'),
      num('long. collet #1 (L2)', 0, 16, 'mm'),
      num('Demi angle du cône du corps BHTA [°]', 0, 90, '°'),
      num('Rayon de rectifiage', 0, 20, 'mm'),
    ],
  },
  {
    id: 'cutting',
    fields: [
      choice('Nb dents', ['1', '2', '3', '4', '5', '6', '7'], true),
      num('hélice 1', -90, 90, '°'),
      num('hélice 2', -90, 90, '°'),
      choice('Sens de coupe', ['D', 'G']),
      choice('Profil', [
        'Lisse',
        'Brise copeaux',
        'Ebauche',
        'Ebauche fin',
        'Ebauche grossier',
        'Ebauche pas 1',
        'Ebauche pas 0.3',
      ]),
      choice('Pas du profil', ['-', '0.3', '1.0']),
      choice('Forme de la fraise', ['-', 'Fermée', 'Appuyée']),
      choice('Coin', ['-', 'Vif', "Cassé d'angle", 'Torique', 'Hémisphérique', 'Pointe']),
      choice('Coupe au centre', ['-', 'Oui', 'Non']),
      choice('Cassé de coupe', ['OUI', 'NON']),
      num('Largeur cassé de coupe', 0.01, 1.5, 'mm'),
    ],
  },
  {
    id: 'angles',
    fields: [
      num('Angle de coupe bout', -30, 30, '°'),
      num('Angle de coupe diamètre mini goujure 1', -30, 30, '°'),
      num('Angle de coupe diamètre maxi goujure 1', -30, 30, '°'),
      num('1ère dépouille bout', 0, 45, '°'),
      num('2ème dépouille bout', 0, 45, '°'),
      num('1ère dépouille diamètre', 0, 45, '°'),
      // "Cylindique" is the worker's own spelling: it is a wire value.
      choice('type de dépouille diamètre', [
        '-',
        'Radiale',
        'Droite',
        'Cylindique',
        'Logarithmique',
      ]),
    ],
  },
  {
    id: 'grinding',
    fields: [
      choice('Affûtage bout', ['-', 'Symétrique', 'Une dent au centre']),
      num('Largeur affûtage bout', 0, 2, 'mm'),
      num('Dépassement du centre', -0.5, 0.5, 'mm'),
      num('Largeur listel', 0, 2, 'mm'),
      num("Largeur d'âme", -0.5, 0.5, 'mm'),
      num('Noyau [mm]', 0, 6, 'mm'),
      num('Noyau arrière', 0, 6, 'mm'),
      num('Creux 1 [°]', -90, 90, '°'),
      num('Creux 2 [°]', -90, 90, '°'),
      num('Creux 3 [°]', -90, 90, '°'),
      num('longueur creux', 0, 3, 'mm'),
      choice('Indexation taillage', ['Régulière', 'Irrégulière']),
      choice('Indexation taillage angle', [
        '-',
        '360',
        '180',
        '120',
        '90',
        '10° 90° 190° 270°',
        '0° 128° 240°',
        '72',
        '60',
      ]),
    ],
  },
  {
    id: 'material',
    fields: [
      choice('Type de fabrication', ['Monobloc', 'Composite']),
      choice('Substrat / matière outil', [
        '-',
        'Carbure',
        'Céramique',
        'Diamant',
        'PCD',
        'CBN',
        'Cermet',
        'HSS',
        'MD K20-K30',
        'K20-K30',
        'C01',
        'CTU08L',
        'EMT100',
        'EMT609',
        'EMT612',
        'H10F',
        'MG10',
        'MD',
        'CERA',
      ]),
      num('% de Co', 0, 100, '%'),
    ],
  },
];

export const BASE_TOOL_FIELDS: BaseToolField[] = BASE_TOOL_GROUPS.flatMap((group) => group.fields);

/** DOM id of a field's control: wire keys hold spaces, brackets and accents. */
export function baseToolFieldId(key: string): string {
  return `base-tool-${key.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`;
}

const FIELD_BY_KEY = new Map(BASE_TOOL_FIELDS.map((field) => [field.key, field]));

/** Spellings seen in user-supplied tools, mapped to the worker's key. */
const KEY_ALIASES: Record<string, string> = {
  'diam. collet #1 (D2)': 'diam. collet  #1 (D2)',
};

/** What the user typed or picked, per wire key. Empty / absent means "not filled yet". */
export type BaseToolDraft = Record<string, string>;

export type BaseToolWire = Record<string, string | number>;

export type FieldIssue = 'missing' | 'notANumber' | 'outOfRange' | 'unknownChoice';

/** Plain decimal notation, with a dot or a comma: no exponent, no hex, no stray text. */
const DECIMAL = /^[+-]?(\d+([.,]\d*)?|[.,]\d+)$/;

export function parseDecimal(raw: string): number | null {
  const text = raw.trim();
  if (!DECIMAL.test(text)) return null;
  const value = Number(text.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

export function fieldIssue(field: BaseToolField, raw: string | undefined): FieldIssue | null {
  const text = (raw ?? '').trim();
  if (text === '') return 'missing';
  if (field.kind === 'choice') return field.options.includes(text) ? null : 'unknownChoice';

  const value = parseDecimal(text);
  if (value === null) return 'notANumber';
  const aboveMin = field.exclusiveMin ? value > field.min : value >= field.min;
  return aboveMin && value <= field.max ? null : 'outOfRange';
}

export interface DraftValidation {
  issues: Record<string, FieldIssue>;
  total: number;
  /** Fields holding a valid value. */
  valid: number;
  /** Fields holding something that is not acceptable (anything but `missing`). */
  invalid: number;
  isComplete: boolean;
}

export function validateDraft(draft: BaseToolDraft): DraftValidation {
  const issues: Record<string, FieldIssue> = {};
  let invalid = 0;
  for (const field of BASE_TOOL_FIELDS) {
    const issue = fieldIssue(field, draft[field.key]);
    if (!issue) continue;
    issues[field.key] = issue;
    if (issue !== 'missing') invalid += 1;
  }
  const total = BASE_TOOL_FIELDS.length;
  const valid = total - Object.keys(issues).length;
  return { issues, total, valid, invalid, isComplete: valid === total };
}

/**
 * The draft as the worker expects it: numbers as numbers, choices as their option text. Fields
 * left empty are omitted; a value that does not parse is kept as typed so that the JSON view
 * shows what the user actually entered.
 */
export function toWireTool(draft: BaseToolDraft): BaseToolWire {
  const tool: BaseToolWire = {};
  for (const field of BASE_TOOL_FIELDS) {
    const text = (draft[field.key] ?? '').trim();
    if (text === '') continue;
    const asNumber = field.kind === 'number' || field.numeric ? parseDecimal(text) : null;
    tool[field.key] = asNumber ?? text;
  }
  return tool;
}

function choiceText(field: ChoiceField, value: unknown): string {
  const text = String(value).trim();
  if (field.options.includes(text) || typeof value !== 'number') return text;
  // `"Pas du profil": 1` must match the option "1.0".
  return field.options.find((option) => Number(option) === value) ?? text;
}

export interface ImportedTool {
  draft: BaseToolDraft;
  /** Keys the worker does not know: it would drop them. */
  unknownKeys: string[];
}

export function fromWireTool(tool: Record<string, unknown>): ImportedTool {
  const draft: BaseToolDraft = {};
  const unknownKeys: string[] = [];
  for (const [rawKey, value] of Object.entries(tool)) {
    const key = KEY_ALIASES[rawKey] ?? rawKey;
    const field = FIELD_BY_KEY.get(key);
    if (!field) {
      unknownKeys.push(rawKey);
      continue;
    }
    if (value === null || value === undefined || value === '') continue;
    draft[key] = field.kind === 'choice' ? choiceText(field, value) : String(value);
  }
  return { draft, unknownKeys };
}

export type ParsedToolJson =
  | ({ ok: true } & ImportedTool)
  | { ok: false; reason: 'syntax'; message: string }
  | { ok: false; reason: 'notAnObject' };

export function parseBaseToolJson(text: string): ParsedToolJson {
  if (text.trim() === '') return { ok: true, draft: {}, unknownKeys: [] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return { ok: false, reason: 'syntax', message: error instanceof Error ? error.message : '' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, reason: 'notAnObject' };
  }
  return { ok: true, ...fromWireTool(parsed as Record<string, unknown>) };
}

export function draftToJson(draft: BaseToolDraft): string {
  return JSON.stringify(toWireTool(draft), null, 2);
}
